import { useEffect, useMemo, useState } from "react";
import { REGIONS } from "./data/collections";
import { track } from "./analytics";
import { useLang } from "./i18n";

// ภาษาไทยเรียง ก–ฮ (collation ภาษาไทยข้ามสระหน้า เ แ โ ใ ไ ให้เอง) / ภาษาอังกฤษเรียง A–Z แล้วตามชื่อรุ่น
const COLLATORS = { th: new Intl.Collator("th"), en: new Intl.Collator("en") };
const byName = (lang) => (a, b) => {
  const c = COLLATORS[lang];
  const key = lang === "en" ? "provinceEn" : "provinceTh";
  return c.compare(a[key], b[key]) || c.compare(a.editionEn || "", b.editionEn || "");
};

const matches = (c, q) =>
  !q || [c.provinceTh, c.provinceEn, c.editionEn].some((t) => t && t.toLowerCase().includes(q));

export default function CollectionGallery({ collections, currentId, onSelect }) {
  const { lang, t } = useLang();
  const [region, setRegion] = useState("all");
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();

  // ส่งคำค้นเมื่อหยุดพิมพ์ 1 วินาที (ไม่ส่งทุกตัวอักษร)
  useEffect(() => {
    if (q.length < 2) return undefined;
    const t = setTimeout(() => track("gallery_search", { search_term: q }), 1000);
    return () => clearTimeout(t);
  }, [q]);

  const pickRegion = (id) => {
    setRegion(id);
    track("gallery_filter", { region: id });
  };
  const shown = useMemo(
    () => collections.filter((c) => (region === "all" || c.region === region) && matches(c, q)).sort(byName(lang)),
    [collections, region, q, lang]
  );
  const counts = useMemo(() => {
    const out = {};
    collections.forEach((c) => {
      out[c.region] = (out[c.region] || 0) + 1;
    });
    return out;
  }, [collections]);

  return (
    <section className="bce-gallery" id="bce-gallery" aria-labelledby="bce-gallery-title">
      <div className="bce-gallery-head">
        <h2 id="bce-gallery-title">Collection Gallery</h2>
        <p>{t("galleryIntro", collections.length)}</p>
      </div>

      <div className="bce-gallery-tools">
        <input
          type="search"
          className="bce-gallery-search"
          placeholder={t("searchPlaceholder")}
          aria-label={t("searchLabel")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="bce-gallery-chips" role="group" aria-label={t("filterLabel")}>
          <button type="button" className={region === "all" ? "is-selected" : ""} aria-pressed={region === "all"} onClick={() => pickRegion("all")}>
            {t("all")} ({collections.length})
          </button>
          {REGIONS.filter((r) => counts[r.id]).map((r) => (
            <button
              key={r.id}
              type="button"
              className={region === r.id ? "is-selected" : ""}
              aria-pressed={region === r.id}
              onClick={() => pickRegion(r.id)}
            >
              {r[lang]} ({counts[r.id]})
            </button>
          ))}
        </div>
      </div>

      <div className="bce-gallery-grid" aria-live="polite">
        {shown.map((c) => {
          const selected = c.id === currentId;
          return (
            <button
              key={c.id}
              type="button"
              className={`bce-gallery-item${selected ? " is-selected" : ""}`}
              aria-current={selected ? "true" : undefined}
              onClick={() => onSelect(c)}
            >
              <span className="bce-gallery-thumb" style={{ "--handle": c.handleColor }}>
                <img src={c.thumbnail} alt="" loading="lazy" />
              </span>
              <b>{lang === "en" ? c.provinceEn : c.provinceTh}</b>
              <small>{c.editionEn || (lang === "en" ? c.provinceTh : c.provinceEn)}</small>
              {selected && <span className="bce-gallery-badge">{t("nowViewing")}</span>}
            </button>
          );
        })}
        {!shown.length && <p className="bce-gallery-empty">{t("notFound")}</p>}
      </div>
    </section>
  );
}
